import { AxiosInstance } from 'axios'
import { Team } from 'types/groups'
import { ID, PaginatedResult } from 'types/utility'
import { Program, Purpose, Rate } from 'types/programs'
import { PROGRAMS_ENDPOINTS, USER_ENDPOINTS } from 'api/ApiRoutes'
import { CreateProgramHistoryPayload, UserProfile } from 'types/user'
import pickBy from 'lodash.pickby'
import { Member } from 'types/members'

class ProgramsApi {
  constructor(private axiosInstance: AxiosInstance) {}

  getPrograms = async ({
    teamId,
    page = 1,
    page_size = 150
  }: {
    teamId: Team['token_id']
    page_size?: number
    page?: number
  }) => {
    const d = await this.axiosInstance.get<PaginatedResult<Program>>(
      PROGRAMS_ENDPOINTS.PROGRAMS(teamId),
      {
        params: { page_size, page }
      }
    )
    return d.data
  }

  getProgram = async ({
    teamId,
    programId
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
  }) => {
    const d = await this.axiosInstance.get<Program>(
      PROGRAMS_ENDPOINTS.PROGRAM(teamId, programId)
    )
    return d.data
  }

  editProgram = async ({
    teamId,
    programId,
    program
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
    program: Partial<Program>
  }) => {
    const d = await this.axiosInstance.put<Program>(
      PROGRAMS_ENDPOINTS.PROGRAM(teamId, programId),
      { program }
    )
    return d.data
  }

  cancelProgramChanges = async ({
    teamId,
    programId
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
  }) => {
    const d = await this.axiosInstance.delete(
      PROGRAMS_ENDPOINTS.PROGRAM_PENDING_CHANGES(teamId, programId)
    )
    return d.data
  }

  createProgram = async ({
    teamId,
    program
  }: {
    teamId: Team['token_id']
    program: Partial<Program>
  }) => {
    const d = await this.axiosInstance.post(
      PROGRAMS_ENDPOINTS.PROGRAMS(teamId),
      {
        program
      }
    )
    return d.data
  }

  archiveProgram = async ({
    teamId,
    programId
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
  }) => {
    const d = await this.axiosInstance.delete(
      PROGRAMS_ENDPOINTS.PROGRAM(teamId, programId)
    )
    return d.data
  }

  getProgramHistory = async ({
    userId
  }: {
    userId: UserProfile['token_id']
  }) => {
    const d = await this.axiosInstance.get(
      USER_ENDPOINTS.USER_PROGRAM_HISTORY_LIST(userId),
      {
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        }
      }
    )
    return d.data
  }

  deleteProgramFromHistory = async ({
    userId,
    historyId
  }: {
    userId: UserProfile['token_id']
    historyId: ID
  }) => {
    const d = await this.axiosInstance.delete(
      USER_ENDPOINTS.USER_PROGRAM_HISTORY(userId, historyId)
    )
    return d.data
  }

  createProgramHistory = async ({
    userId,
    program_history
  }: {
    userId: UserProfile['token_id']
    program_history: CreateProgramHistoryPayload
  }) => {
    const payload = pickBy(program_history, (value) => value !== null)
    const d = await this.axiosInstance.post(
      USER_ENDPOINTS.USER_PROGRAM_HISTORY_LIST(userId),
      {
        user_program_history: payload
      }
    )
    return d.data
  }

  getProgramRates = async ({
    teamId,
    programId,
    memberId
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
    memberId: UserProfile['token_id'] | Member['token_id']
  }) => {
    const d = await this.axiosInstance.get<PaginatedResult<Rate>>(
      PROGRAMS_ENDPOINTS.PROGRAM_RATES(teamId, programId),
      {
        params: {
          user_id: memberId
        }
      }
    )
    return d.data
  }

  createRate = async ({
    teamId,
    programId,
    rate
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
    rate: {
      rate: Rate['miles_values']['rate'] | Rate['kms_values']['rate']
      effective_date: Rate['effective_date']
    }
  }) => {
    const d = await this.axiosInstance.post(
      PROGRAMS_ENDPOINTS.PROGRAM_RATES(teamId, programId),
      { rate }
    )
    return d.data
  }

  deleteRate = async ({
    teamId,
    programId,
    rateId
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
    rateId: Rate['token_id']
  }) => {
    const d = await this.axiosInstance.delete(
      PROGRAMS_ENDPOINTS.PROGRAM_RATE(teamId, programId, rateId)
    )
    return d.data
  }

  getProgramPurposes = async ({
    teamId,
    programId
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
  }) => {
    const d = await this.axiosInstance.get<PaginatedResult<Purpose>>(
      PROGRAMS_ENDPOINTS.PROGRAM_PURPOSES(teamId, programId)
    )
    return d.data
  }

  editProgramPurpose = async ({
    teamId,
    programId,
    purposeId,
    purpose
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
    purposeId: Purpose['token_id']
    purpose: Partial<Purpose>
  }) => {
    const d = await this.axiosInstance.put(
      PROGRAMS_ENDPOINTS.PROGRAM_PURPOSE(teamId, programId, purposeId),
      {
        purpose
      }
    )
    return d.data
  }

  createProgramPurpose = async ({
    teamId,
    programId,
    purpose
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
    purpose: Partial<Purpose>
  }) => {
    const d = await this.axiosInstance.post(
      PROGRAMS_ENDPOINTS.PROGRAM_PURPOSES(teamId, programId),
      {
        purpose
      }
    )
    return d.data
  }

  deleteProgramPurpose = async ({
    teamId,
    programId,
    purposeId
  }: {
    teamId: Team['token_id']
    programId: Program['token_id']
    purposeId: Purpose['token_id']
  }) => {
    const d = await this.axiosInstance.delete(
      PROGRAMS_ENDPOINTS.PROGRAM_PURPOSE(teamId, programId, purposeId)
    )
    return d.data
  }
}

export default ProgramsApi
